import { Content, Editor } from "@tiptap/react";
import {
  RecommendationsSuggestions_Recommendation,
  WebResult,
} from "protogen/suggestions_pb";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { NodeViewProps } from "@tiptap/react";
import { EditorView } from "@tiptap/pm/view";

export interface TipTapNodeAttr extends Attr {}

export interface TipTapComponentNodeProps<T extends TipTapNodeAttr>
  extends ProseMirrorNode {
  attrs: T;
}

export interface TipTapComponentProps<T extends TipTapNodeAttr>
  extends NodeViewProps {
  node: TipTapComponentNodeProps<T>;
}

export type InsertableSuggestion = {
  product?: WebResult;
  webLink?: WebResult;
  recommendation?: RecommendationsSuggestions_Recommendation;
  text?: string;
};

const appendContent = (
  editor: Editor,
  content: Content,
  addParagraph: boolean = true,
) => {
  const endOfDoc = editor.state.doc.content.size;
  const chain = editor?.chain().insertContentAt(endOfDoc, content);
  if (addParagraph) {
    chain.insertContentAt(endOfDoc + 1, {
      type: "paragraph",
    });
  }
  chain.run();
};

const insertRecommendation = (
  editor: Editor,
  recommendation: RecommendationsSuggestions_Recommendation,
) => {
  appendContent(editor, {
    type: "recommendationExtension",
    attrs: {
      identifier: recommendation.postRef,
      title: recommendation.title,
      subtitle: recommendation.textContent,
      url: `/community/${encodeURIComponent(recommendation.postRef)}`,
    },
  });
};

const insertProduct = (editor: Editor, product: WebResult) => {
  appendContent(editor, {
    type: "productLinkExtension",
    attrs: {
      identifier: product.url,
      title: product.title,
      description: product.description,
      url: product.url,
      thumbnailUrl: product.thumbnailUrl,
      phone: product.phone,
      address: product.address,
    },
  });
};

const insertText = (
  editor: Editor,
  text: string,
  addParagraph: boolean = true,
) => {
  appendContent(
    editor,
    {
      type: "paragraph",
      content: [{ type: "text", text }],
    },
    addParagraph,
  );
};

export const insertSuggestion = (
  editor: Editor,
  suggestion: InsertableSuggestion,
) => {
  if (suggestion.product) {
    insertProduct(editor, suggestion.product);
  } else if (suggestion.webLink) {
    insertProduct(editor, suggestion.webLink);
  } else if (suggestion.recommendation) {
    insertRecommendation(editor, suggestion.recommendation);
  } else if (suggestion.text) {
    insertText(editor, suggestion.text, false);
  }
};

export const getEditorWidth = (view: EditorView) => {
  const editorElement = view.dom.closest(".ProseMirror");
  if (editorElement) {
    return editorElement.getBoundingClientRect().width;
  }
  return null;
};
